import { makeAutoObservable, runInAction } from 'mobx'
import {
  BadgeFilter,
  BadgeLoadingType,
  BadgePayload,
  BadgesType,
} from './types'
import { handleAxiosError } from '~/shared/lib'
import { BadgesServices } from '~/shared/api/services/badges'

const INITIAL_FORM_BADGES = {
  id: '',
  color: '#FF0000',
  description: '',
  localizations: [{ description: '', languageCode: 'RU' }],
  textColor: '#000',
}

export class BadgesStore {
  badges: BadgesType[] = []

  loaders: BadgeLoadingType = {
    badges: false,
    loadMoreBadges: false,
    createBadge: false,
    badgeById: false,
    updateBadge: false,
    removeBadge: false,
  }

  formData: BadgesType = INITIAL_FORM_BADGES

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  searchParams: BadgeFilter = {
    q: '',
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof BadgeLoadingType) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof BadgeLoadingType) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = (data: BadgesType) => {
    this.formData = { ...this.formData, ...data }
  }

  resetFormData = () => {
    this.formData = INITIAL_FORM_BADGES
  }

  setSearchParam = <K extends keyof BadgeFilter>(
    key: K,
    value: BadgeFilter[K],
  ) => {
    this.searchParams[key] = value
  }

  setFormBadgesData = (
    key: keyof BadgesType,
    value: BadgesType[keyof BadgesType],
  ) => {
    this.formData = { ...this.formData, [key]: value }
  }

  setFormDataLocalizations = (languageCode: string, value: string) => {
    this.formData = {
      ...this.formData,
      localizations: this.formData.localizations.some(
        (localization) => localization.languageCode === languageCode,
      )
        ? this.formData.localizations.map((localization) =>
            localization.languageCode === languageCode
              ? { ...localization, description: value }
              : localization,
          )
        : [
            ...this.formData.localizations,
            { languageCode, description: value },
          ],
    }
  }

  getBadges = async (options?: {
    action?: 'loadMoreBadges'
    filters?: BadgeFilter
  }) => {
    const { action, filters } = options || {}
    if (action === 'loadMoreBadges' && this.requestSettings.isAllLoaded) return
    this.activeLoader(action || 'badges')

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action !== 'loadMoreBadges' ? 0 : this.requestSettings.offset,
        ...filters,
      }

      const { data } = await BadgesServices.getBadges({ params })

      if (action === 'loadMoreBadges') {
        runInAction(() => {
          this.badges = [...this.badges, ...data]
        })
      } else {
        runInAction(() => {
          this.badges = data
        })
      }
      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreBadges'
              ? this.requestSettings.offset + this.requestSettings.limit
              : 20,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'badges')
    }
  }

  createBadge = async (badge: BadgePayload) => {
    try {
      this.activeLoader('createBadge')
      await BadgesServices.createBadge(badge)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createBadge')
    }
  }

  getBadgeById = async (id: string) => {
    this.activeLoader('badgeById')
    try {
      const { data } = await BadgesServices.getBadgeById(id)

      runInAction(() => {
        this.formData = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('badgeById')
    }
  }

  deleteBadge = async (id: string) => {
    this.activeLoader('removeBadge')
    try {
      await BadgesServices.removeBadge(id)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('removeBadge')
    }
  }

  updateBadge = async (id: string) => {
    this.activeLoader('updateBadge')
    try {
      await BadgesServices.updateBadge(id, {
        ...this.formData,
        id: this.formData.id || null,
        color: this.formData.color || null,
        description: this.formData.description || null,
        textColor: this.formData.textColor || null,
        localizations: this.formData.localizations.map((localization) => ({
          ...localization,
          description: localization.description || null,
        })),
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('updateBadge')
    }
  }
}
